import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Chart, registerables } from "chart.js";
import "chartjs-adapter-moment";
import { useAuth } from "../../../../contexts/userContext";

Chart.register(...registerables);

const API_URL = process.env.REACT_APP_API_URL;

const fetchCourses = async () => {
  const response = await fetch(`${API_URL}/my-courses`, {
    credentials: "include",
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error || "Failed to fetch courses");
  return result.myCourses;
};

const loadCourseProgress = async ({ queryKey }) => {
  const [, userId, courseId] = queryKey;

  const response = await fetch(`${API_URL}/progress/summary/${userId}/${courseId}`, {
    credentials: "include",
  });

  const result = await response.json();
  if (!response.ok) throw new Error(result.error || "Failed to fetch progress");
  return result;
};

export function useCourseProgress() {
  const { user } = useAuth();
  const [selectedCourseId, setSelectedCourseId] = useState(null);

  // Chart instance refs (Chart.js objects, for destroy-on-update)
  const weeklyChartRef = useRef(null);
  const completionChartRef = useRef(null);
  const timeDistChartRef = useRef(null);

  // Canvas element refs (attach these to <canvas> in JSX)
  const weeklyChartCanvas = useRef(null);
  const completionChartCanvas = useRef(null);
  const timeDistChartCanvas = useRef(null);

  const {
    data: courses = [],
    isLoading: coursesLoading,
    isError: coursesError,
  } = useQuery({
    queryKey: ["myCourses"],
    queryFn: fetchCourses,
    staleTime: 1000 * 60 * 5,
  });

  const {
    data: progressData,
    isLoading: progressLoading,
    isError: progressError,
  } = useQuery({
    queryKey: ["courseProgress", user?.user_id, selectedCourseId],
    queryFn: loadCourseProgress,
    enabled: !!user?.user_id && !!selectedCourseId,
    staleTime: 3 * 1000 * 60,
  });

  // Initialize with first course
  useEffect(() => {
    if (courses.length > 0 && !selectedCourseId) {
      setSelectedCourseId(courses[0].course_id);
    }
  }, [courses, selectedCourseId]);

  const handleCourseChange = (courseId) => {
    setSelectedCourseId(parseInt(courseId));
  };

  // Render charts when progress data changes
  useEffect(() => {
    if (!progressData) return;

    if (weeklyChartRef.current) weeklyChartRef.current.destroy();
    if (completionChartRef.current) completionChartRef.current.destroy();
    if (timeDistChartRef.current) timeDistChartRef.current.destroy();

    // Weekly Activity Chart
    if (weeklyChartCanvas.current) {
      weeklyChartRef.current = new Chart(weeklyChartCanvas.current, {
        type: "line",
        data: {
          labels: progressData.weeklyActivity.map((d) => d.day_name),
          datasets: [
            {
              label: "Minutes Learned",
              data: progressData.weeklyActivity.map((d) => d.minutes_learned),
              backgroundColor: "rgba(74, 111, 165, 0.2)",
              borderColor: "rgba(74, 111, 165, 1)",
              borderWidth: 2,
              tension: 0.3,
              fill: true,
            },
          ],
        },
        options: {
          responsive: true,
          plugins: { legend: { display: false } },
          scales: {
            y: {
              beginAtZero: true,
              title: { display: true, text: "Minutes" },
            },
          },
        },
      });
    }

    // Completion Progress Chart
    if (completionChartCanvas.current) {
      const totalLessonsInCourse =
        courses.find((course) => course.course_id === selectedCourseId)?.number_of_lessons || 0;

      completionChartRef.current = new Chart(completionChartCanvas.current, {
        type: "doughnut",
        data: {
          labels: ["Completed", "Remaining"],
          datasets: [
            {
              data: [
                progressData.completedLessons,
                totalLessonsInCourse - progressData.completedLessons,
              ],
              backgroundColor: ["rgba(76, 175, 80, 0.7)", "rgba(211, 211, 211, 0.7)"],
              borderColor: ["rgba(76, 175, 80, 1)", "rgba(169, 169, 169, 1)"],
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          plugins: {
            legend: { position: "bottom" },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const label = context.label || "";
                  const value = context.raw || 0;
                  const total = context.dataset.data.reduce((a, b) => a + b, 0);
                  const percentage = Math.round((value / total) * 100);
                  return `${label}: ${value} (${percentage}%)`;
                },
              },
            },
          },
          cutout: "70%",
        },
      });
    }

    // Time Distribution Chart (canvas not currently rendered in EnrolledCourses JSX — see note above)
    if (timeDistChartCanvas.current) {
      const timeDistribution = {
        Videos: Math.floor((progressData.totalTime * 0.6) / 60),
        Readings: Math.floor((progressData.totalTime * 0.25) / 60),
        Exercises: Math.floor((progressData.totalTime * 0.15) / 60),
      };

      timeDistChartRef.current = new Chart(timeDistChartCanvas.current, {
        type: "bar",
        data: {
          labels: Object.keys(timeDistribution),
          datasets: [
            {
              label: "Minutes Spent",
              data: Object.values(timeDistribution),
              backgroundColor: [
                "rgba(255, 126, 95, 0.7)",
                "rgba(74, 111, 165, 0.7)",
                "rgba(106, 176, 76, 0.7)",
              ],
              borderColor: [
                "rgba(255, 126, 95, 1)",
                "rgba(74, 111, 165, 1)",
                "rgba(106, 176, 76, 1)",
              ],
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          plugins: { legend: { display: false } },
          scales: {
            y: {
              beginAtZero: true,
              title: { display: true, text: "Minutes" },
            },
          },
        },
      });
    }

    return () => {
      if (weeklyChartRef.current) weeklyChartRef.current.destroy();
      if (completionChartRef.current) completionChartRef.current.destroy();
      if (timeDistChartRef.current) timeDistChartRef.current.destroy();
    };
  }, [progressData]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  return {
    courses,
    coursesLoading,
    coursesError,
    selectedCourseId,
    handleCourseChange,
    progressData,
    progressLoading,
    progressError,
    weeklyChartCanvas,
    completionChartCanvas,
    timeDistChartCanvas,
    formatTime,
  };
}